import { Directive, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { Cart, CartActions, CartLine, CartLineSources, CartSelectors, GetDeliveryMomentsWithSkuResponse, OrderTypes } from '@next/core-lib/cart';
import { FeatureFlagService } from '@next/core-lib/featureflag';
import { AgrDataSource, TableSelectCheckboxDirective } from '@next/core-lib/table-v2';
import { UserSelectors } from '@next/core-lib/user';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';

import { filter, first, map, startWith, switchMap, take } from 'rxjs/operators';
import { SkuCatalogItemViewData } from 'src/app/product/models/product.models';
import { ShopFeatureFlags } from 'src/app/shared/shop-feature-flags';
import { validateSiloDistribution } from 'src/app/validators/silo-distribution.validator';

import { Environment } from 'src/environments/environment.model';

import { RepeatOrderItem, RepeatOrderItemVM } from '../../../models/repeat-order-item.model';
import { selectOrderHistorySkuInformation, selectProposalDeliveryMomentsAndZeroFlex } from '../../../product/store/selectors';
import { openRepeatOrderDeliveryDateDialog, openRepeatOrderSiloDistributionDialog, repeatOrdersRequest } from '../../../store/actions';
import { selectRepeatOrder } from '../../../store/selectors';
import { parents } from '../../../utils/dom';
import { validateDeliveryInformationRequired } from '../../../validators/delivery-information-required.validator';
import { validateStep } from '../../../validators/step.validator';
import { BulkRepeatOrderDataSource } from '../data-source/bulk-repeat-order.data-source';
import { GeneralCargoRepeatOrderDataSource } from '../data-source/general-cargo-repeat-order.data-source';

interface ViewModel {
  prefix: 'bulk' | 'general-cargo';
  dataSource: AgrDataSource<RepeatOrderItemVM>;
  columnsToDisplay: string[];
  form: FormArray;
  cart: Cart | undefined;
  selectedItems: RepeatOrderItemVM[];
}

export enum RepeatOrderType {
  bulk = 'bulk',
  generalCargo = 'general-cargo',
}

export enum RepeatOrderAggregationRequest {
  none = 'none',
  generalCargo = 'general-cargo',
  bulk = 'bulk',
}

@UntilDestroy({ checkProperties: true })
@Directive()
export abstract class AbstractRepeatOrderPageComponent implements OnInit {
  @ViewChildren(TableSelectCheckboxDirective) checkboxes: QueryList<unknown> | undefined;

  public isFavoritesEnabled = false;

  readonly hasAtLeastAdvisorRole$ = this.store.select(AuthorizationSelectors.hasAtLeastAdvisorRole);

  readonly $vm: Observable<ViewModel>;

  private readonly selectionChangedSubject = new BehaviorSubject<RepeatOrderItemVM[]>([]);
  private cart$ = this.store.select(CartSelectors.selectCurrentSalesOrderCart);
  private customer$ = this.store.select(UserSelectors.selectCustomer);

  private cachedForm = new FormArray([]);
  private cacheFormKey = '';

  private defaultTableColumns: string[] = ['name', 'data', 'quantity', 'deliveryInformation', 'favoriteStar', 'addSingleItemToCart'];
  private form$: Observable<FormArray>;
  private siloDistributionColumnName = 'siloDistribution';
  private palletInformationColumnName = 'palletInformation';

  protected constructor(
    readonly store: Store,
    readonly env: Environment,
    protected repeatOrderType: RepeatOrderType,
    private readonly router: Router,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    const dataSource =
      repeatOrderType === RepeatOrderType.bulk
        ? new BulkRepeatOrderDataSource(store, this.hasAtLeastAdvisorRole$, env)
        : new GeneralCargoRepeatOrderDataSource(store, this.hasAtLeastAdvisorRole$, env);

    this.form$ = dataSource.connect().pipe(
      map((data) => {
        const form = this.createOrCachedForm(data);
        this.patchFormValues(form, data);

        return form;
      }),
    );

    this.$vm = combineLatest([this.form$, this.selectionChangedSubject, this.cart$]).pipe(
      switchMap(([form, selection, cart]) =>
        form.valueChanges.pipe(
          startWith(true),
          map(() => [form, this.getValidSelection(selection, form), cart]),
        ),
      ),
      map(([form, selection, cart]: [FormArray, RepeatOrderItemVM[], Cart]) => {
        const tableColumns = this.defaultTableColumns;

        if (repeatOrderType === RepeatOrderType.bulk && !tableColumns.includes(this.siloDistributionColumnName)) {
          tableColumns.splice(3, 0, this.siloDistributionColumnName);
        }

        if (repeatOrderType === RepeatOrderType.generalCargo && !tableColumns.includes(this.palletInformationColumnName)) {
          tableColumns.splice(2, 0, this.palletInformationColumnName);
        }

        return {
          prefix: repeatOrderType,
          dataSource,
          form,
          columnsToDisplay: tableColumns,
          cart,
          selectedItems: selection,
        };
      }),
    );
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(selectRepeatOrder),
      this.store.select(selectOrderHistorySkuInformation),
      this.store.select(selectProposalDeliveryMomentsAndZeroFlex),
    ])
      .pipe(first())
      .subscribe(([{ bulk, generalCargo }, skuCatalogItems, deliveryMoments]) => {
        const repeatOrderItems = this.repeatOrderType === RepeatOrderType.bulk ? bulk : generalCargo;
        const numberOfItemsWithRetrievedData = this.determineNumberOfItemsWithRetrievedData(repeatOrderItems, deliveryMoments.deliveryDates, skuCatalogItems);
        if (repeatOrderItems.length === 0 || numberOfItemsWithRetrievedData !== repeatOrderItems.length) {
          this.store.dispatch(repeatOrdersRequest({ aggregationType: this.mapRepeatOrderToAggregationType(this.repeatOrderType) }));
        }
      });

    this.customer$
      .pipe(
        map((selectedCustomer) => selectedCustomer?.shipTos[0]?.salesAreas[0]?.salesOrganization),
        switchMap((salesOrganization) => this.featureFlagService.getFeatureFlagsForSalesArea(salesOrganization)),
      )
      .subscribe((featureFlags) => (this.isFavoritesEnabled = featureFlags.includes(ShopFeatureFlags.enableFavorites)));
  }

  addSelectedItemsToCart(cart: Cart | undefined, selectedItems: RepeatOrderItemVM[], form: FormArray) {
    if (cart) {
      selectedItems.forEach((item) => this.addSingleItemToCart(cart, item, form));
    } else {
      this.createNewCartWithItems(selectedItems, form);
    }

    this.store
      .select(CoreSelectors.selectLoading)
      .pipe(
        filter((loading) => !loading),
        take(1),
      )
      .subscribe(() => this.openCart(cart?.id));
  }

  addSingleItemToCart(cart: Cart | undefined, item: RepeatOrderItemVM, form: FormArray) {
    if (cart) {
      this.addItemToExistingCart(cart, item, form);
    } else {
      this.createNewCartWithItems([item], form);
    }
  }

  openCart(cartId: number): void {
    this.router.navigate(['/cart'], { queryParams: { cartId } });
  }

  isEquals(a: RepeatOrderItemVM, b: RepeatOrderItemVM) {
    return a.internalId === b.internalId;
  }

  onSelectionChanged(selection: RepeatOrderItemVM[]) {
    this.selectionChangedSubject.next(selection);
  }

  selectRow(event: Event, element: HTMLElement) {
    event.preventDefault();
    event.stopPropagation();

    if (element.className.includes('--disabled')) {
      return;
    }

    const row = parents(element, 'tr');
    (row?.[0]?.querySelector('.mat-column-select input') as HTMLInputElement)?.click();
  }

  openSiloDistributionDialog(repeatOrderItem: RepeatOrderItemVM) {
    const formItem = this.cachedForm.value.find((it: { id: number; quantity: number }) => it.id === repeatOrderItem.internalId);
    const quantity: number = formItem?.quantity;
    this.store.dispatch(openRepeatOrderSiloDistributionDialog({ repeatOrderItem, quantity }));
  }

  openDeliveryDateDialog(repeatOrderItem: RepeatOrderItemVM, formGroup: FormGroup) {
    this.store.dispatch(
      openRepeatOrderDeliveryDateDialog({
        repeatOrderItem,
        repeatOrderType: this.repeatOrderType,
        deliveryInformation: formGroup.get('deliveryInformation').value ?? undefined,
      }),
    );
  }

  getSiloIds(row: RepeatOrderItem): string {
    return row.lastUsedSilos?.map((silo) => silo.siloNumber).join(', ');
  }

  private addItemToExistingCart(cart: Cart, item: RepeatOrderItemVM, form: FormArray) {
    const cartLine = this.mapRepeatOrderItem(item, form);

    this.store.dispatch(
      CartActions.addCartLineToCart({
        cartLine,
        cartId: cart.id,
      }),
    );
  }

  private createNewCartWithItems(items: RepeatOrderItemVM[], form: FormArray) {
    const cartLines = this.mapRepeatOrderItems(items, form);

    this.store.dispatch(
      CartActions.postCartRequest({
        cart: {
          id: null,
          type: OrderTypes.salesOrder,
          lines: cartLines,
        },
      }),
    );
  }

  private mapRepeatOrderItems(repeatOrderItems: RepeatOrderItemVM[], form: FormArray) {
    return repeatOrderItems.map((repeatOrderItem) => this.mapRepeatOrderItem(repeatOrderItem, form)).filter((line) => !!line);
  }

  private mapRepeatOrderItem(repeatOrderItem: RepeatOrderItemVM, form: FormArray) {
    const formItem = form.value.find((it) => it.id === repeatOrderItem.internalId);
    const { quantity, deliveryInformation, silos } = formItem;

    if (quantity) {
      return {
        skuId: repeatOrderItem.internalId,
        quantity,
        silos,
        deliveryInformation,
        source: CartLineSources.REPEAT_ORDER,
      } as CartLine;
    } else {
      console.warn(`Unknown quantity for ${repeatOrderItem.internalId}`);
    }
  }

  private determineNumberOfItemsWithRetrievedData(
    repeatOrderItems: RepeatOrderItem[],
    deliveryMoments: GetDeliveryMomentsWithSkuResponse[],
    skuCatalogItems: SkuCatalogItemViewData[],
  ) {
    const skuIdsWithCatalogData = repeatOrderItems.filter((item) => skuCatalogItems.some((x) => x.skuId === item.internalId)).map((item) => item.internalId);
    const skuIdsWithDeliveryData = repeatOrderItems.filter((item) => deliveryMoments.some((x) => x.skuId === item.internalId)).map((item) => item.internalId);
    return [...new Set([...skuIdsWithCatalogData, ...skuIdsWithDeliveryData])].length;
  }

  private createOrCachedForm(data: readonly RepeatOrderItemVM[]) {
    const newCacheKey = data
      .map((it) => it.internalId)
      .sort()
      .join('_');

    if (newCacheKey !== this.cacheFormKey) {
      const controls = data.map(
        (item) =>
          new FormGroup(
            {
              id: new FormControl(item.internalId),
              quantity: new FormControl(
                null,
                [
                  Validators.required,
                  item.orderingInformation?.increaseOrderUnitQuantity
                    ? validateStep(item.orderingInformation?.increaseOrderUnitQuantity, item.orderingInformation?.maximumOrderUnitQuantity)
                    : undefined,
                  item.orderingInformation?.minimumOrderUnitQuantity ? Validators.min(item.orderingInformation?.minimumOrderUnitQuantity) : undefined,
                  item.orderingInformation?.maximumOrderUnitQuantity ? Validators.max(item.orderingInformation?.maximumOrderUnitQuantity) : undefined,
                ].filter((it) => !!it),
              ),
              silos: new FormControl(item.silos, this.repeatOrderType === RepeatOrderType.bulk ? [Validators.required] : null),
              deliveryInformation: new FormControl(null, [validateDeliveryInformationRequired()]),
              orderingInformation: new FormControl(item.orderingInformation),
            },
            [validateSiloDistribution],
          ),
      );

      this.cachedForm = new FormArray(controls);
      this.cacheFormKey = newCacheKey;
    }

    return this.cachedForm;
  }

  private patchFormValues(form: FormArray, data: RepeatOrderItemVM[]) {
    const ordersWithSiloDistribution = data.filter((order) => order.silos?.length);
    if (ordersWithSiloDistribution.length) {
      ordersWithSiloDistribution.forEach((repeatOrderItem) => {
        const formControl = form.controls.find((control) => control?.value?.id === repeatOrderItem.internalId);
        formControl?.get('silos')?.patchValue(repeatOrderItem.silos);
      });
    }

    const ordersWithDeliveryInformation = data.filter((order) => order.deliveryInformation);
    if (ordersWithDeliveryInformation.length) {
      ordersWithDeliveryInformation.forEach((repeatOrderItem) => {
        const formControl = form.controls.find((control) => control?.value?.id === repeatOrderItem.internalId);
        formControl?.get('deliveryInformation')?.patchValue(repeatOrderItem.deliveryInformation);
      });
    }
  }

  private getValidSelection(selection: RepeatOrderItemVM[], form: FormArray) {
    return selection.filter((item) => {
      const isUserAllowedToOrderItem = item.isUserAllowedToOrderItem;
      const controlIsValid = form.controls.find((it) => +it.value?.id === item.internalId)?.valid;

      return isUserAllowedToOrderItem && controlIsValid;
    });
  }

  private mapRepeatOrderToAggregationType(orderType: RepeatOrderType): RepeatOrderAggregationRequest {
    switch (orderType) {
      case RepeatOrderType.bulk:
        return RepeatOrderAggregationRequest.bulk;
      case RepeatOrderType.generalCargo:
        return RepeatOrderAggregationRequest.generalCargo;
      default:
        return RepeatOrderAggregationRequest.none;
    }
  }
}
